import React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import BottomNav from "src/components/bottom-nav.component";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LuLeaf, LuUsers, LuLock, LuCoffee, LuSun, LuMic, LuMapPin } from "react-icons/lu";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import IP from "../../sections/variables";

// event days; `key` is the field the agenda API returns for that day
export const DAYS = [
  { key: "day1", date: new Date(2026, 10, 12) },
  { key: "day2", date: new Date(2026, 10, 13) },
  { key: "day3", date: new Date(2026, 10, 14) },
];

const HIGHLIGHTS = [
  { title: "Welcome & Objectives", subtitle: "Setting the tone", Icon: LuLeaf },
  { title: "Board Strategy Session", subtitle: "Key priorities & outlook", Icon: LuUsers },
  { title: "Closed Session", subtitle: "Governance & approvals", Icon: LuLock },
  { title: "Working Lunch", subtitle: "Informal discussion", Icon: LuCoffee },
  { title: "Afternoon Working Session", subtitle: "Breakouts & synthesis", Icon: LuSun },
];

// shown only in `npm start` when the API is unreachable, so the screen can be previewed
const SAMPLE_DAY = [
  { title: "Guest arrival check In", starting_time: "15:00:00", ending_time: "18:00:00", duration: "3 Hours", venue: "Bab Samhan", external_attendees: "Open Arrival Window" },
  { title: "Macro & banking trends shaping SNB's strategy", speaker: "Raja Asad Khan/ McKinsey", starting_time: "08:20:00", ending_time: "08:50:00", duration: "30 Mins", description: "Macro and sectoral economic trends · Banking sector trends · Implications for SNB" },
  { title: "Coffee Break", starting_time: "09:30:00", ending_time: "09:40:00", duration: "10 Mins" },
  { title: "Welcome dinner", starting_time: "20:00:00", ending_time: "22:00:00", duration: "2 Hours", venue: "Flamingo Room Resturant", external_attendees: "8 - 10 minute walk" },
  { title: "End day 1 programme", starting_time: "22:00:00", ending_time: "22:00:00" },
];

const isToday = (d) => d.toDateString() === new Date().toDateString();

// 1-based event day for today, or 1 outside the event
export const todayDay = () => Math.max(DAYS.findIndex((d) => isToday(d.date)), 0) + 1;

// "07:00 AM" / "14:30" -> minutes since midnight
const toMinutes = (h, m, ampm) => {
  let hour = +h % 24;
  if (ampm) hour = (+h % 12) + (/pm/i.test(ampm) ? 12 : 0);
  return hour * 60 + +m;
};

// API sends "07:00 AM to 08:00 AM"; returns { start, end } in minutes (end may be null)
export const parseRange = (time) => {
  const found = [...(time || "").matchAll(/(\d{1,2}):(\d{2})\s*(AM|PM)?/gi)];
  if (!found.length) return null;
  const [start, end] = found.map(([, h, m, ampm]) => toMinutes(h, m, ampm));
  return { start, end: end ?? null };
};

const hhmm = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

// prefer the API's "15:00:00" fields; fall back to parsing the display string.
// end is null when missing or equal to start (e.g. "End day 1 programme")
const eventRange = (e) => {
  const r = e.starting_time ? parseRange(`${e.starting_time} ${e.ending_time || ""}`) : parseRange(e.time);
  if (r && r.end === r.start) r.end = null;
  return r;
};

// index of the session happening now (only for today's agenda), else -1
const currentIndex = (events, date) => {
  if (!isToday(date)) return -1;
  const now = new Date().getHours() * 60 + new Date().getMinutes();
  let idx = -1;
  events.forEach((e, i) => {
    const r = eventRange(e);
    if (r && r.start <= now) idx = i;
  });
  return idx;
};

const AgendaPage = () => {

  document.title = "Meeting Agenda | SNB";

  const navigate = useNavigate();
  const todayIdx = DAYS.findIndex((d) => isToday(d.date));

  return (
    <div className="home-wrapper app-wrapper agenda-page has-bottom-nav">
      <div className="page-content">
        <div className="agenda-topbar">
          <ArrowBackIcon role="button" onClick={() => navigate("/home")} />
        </div>

        <h1 className="agenda-title">Meeting Agenda</h1>

        <div className="agenda-dates">
          {DAYS.map((d, i) => (
            <button
              key={d.key}
              className={`agenda-date ${i === todayIdx ? "active" : ""}`}
              onClick={() => navigate(`/agenda/${i + 1}`)}
            >
              <span className="agenda-date-day">{d.date.getDate()}</span>
              {d.date.toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
            </button>
          ))}
        </div>

        <ul className="agenda-list">
          {HIGHLIGHTS.map(({ title, subtitle, Icon }) => (
            <li key={title}>
              <span className="agenda-icon-badge"><Icon className="agenda-list-icon" /></span>
              <div>
                <div>{title}</div>
                <div className="agenda-muted">{subtitle}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      <BottomNav />
    </div>
  );
};

export const AgendaDayPage = () => {

  const navigate = useNavigate();
  const { day } = useParams();
  const current = DAYS[+day - 1];

  const URL = `${window.location.protocol}//${IP}/api`;
  const { data, isLoading, isError } = useQuery(
    "agenda",
    async () => await axios.get(`${URL}/agenda/list-create`)
  );

  if (!current) return <Navigate to="/agenda" replace />;

  document.title = "Agenda | SNB";

  const devSample = isError && process.env.NODE_ENV === "development";
  const events = devSample ? SAMPLE_DAY : data?.data?.[current.key] ?? [];
  const active = currentIndex(events, current.date);

  const heading = isToday(current.date)
    ? "Today"
    : current.date.toLocaleDateString("en-GB", { weekday: "long" });

  return (
    <div className="home-wrapper app-wrapper agenda-page">
      <div className="page-content">
        <div className="agenda-topbar">
          <ArrowBackIcon role="button" onClick={() => navigate("/agenda")} />
          <h1 className="agenda-day-title">{heading}</h1>
          <span style={{ width: 24 }} />
        </div>

        <div className="agenda-day-date">
          {current.date.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}
        </div>

        <button className="agenda-restaurant-link" onClick={() => navigate(`/restaurant/${day}`)}>
          <RestaurantOutlinedIcon className="agenda-list-icon" />
          <span>Restaurant</span>
          <ChevronRightRoundedIcon />
        </button>

        {isLoading && <p className="agenda-muted">Loading agenda...</p>}
        {isError && !devSample && <p className="text-danger">Failed to load agenda data.</p>}
        {!isLoading && !isError && events.length === 0 && (
          <p className="agenda-muted">No meetings scheduled for this day.</p>
        )}

        <ul className="agenda-timeline">
          {events.map((event, idx) => {
            const range = eventRange(event);
            const topics = (event.description || "").split("·").map((t) => t.trim()).filter(Boolean);
            return (
            <li key={event.id ?? idx} className={idx === active ? "active" : ""}>
              <span className="agenda-time">{range ? hhmm(range.start) : event.time}</span>
              <span className="agenda-dot" />
              <div>
                <div dangerouslySetInnerHTML={{ __html: event.title }} />
                {range?.end != null && (
                  <div className="agenda-until">
                    Until {hhmm(range.end)}{event.duration && ` · ${event.duration}`}
                  </div>
                )}
                {event.speaker && (
                  <div className="agenda-speaker"><LuMic /> {event.speaker}</div>
                )}
                {topics.length > 0 && (
                  <ul className="agenda-topics">
                    {topics.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                )}
                {event.venue && <div className="agenda-venue"><LuMapPin /> {event.venue}</div>}
                {event.external_attendees && <div className="agenda-note">{event.external_attendees}</div>}
              </div>
            </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default AgendaPage;
